// File path: /modules/elevate/frontend/src/services/userService.ts

import { apiHelpers, PaginatedResponse } from './api';

// Import types from the user types file
import type {
  User,
  CreateUserData,
  UpdateUserData,
  UserListParams,
  UserResponse,
  BulkCreatePreview,
  BulkCreateResult,
  TeacherCoachAssignment,
  TeacherCoachAssignmentResponse
} from '../types/user';

// Re-export types for backwards compatibility
export type {
  User,
  CreateUserData,
  UpdateUserData,
  UserListParams,
  UserResponse,
  BulkCreatePreview,
  BulkCreateResult,
  TeacherCoachAssignment,
  TeacherCoachAssignmentResponse
};

export interface Organization {
  id: string;
  name: string;
  org_type: 'state' | 'district' | 'school' | 'program';
  external_id?: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
}

class UserService {
  private basePath = '/users';
  private schoolsPath = '/schools';
  private districtsPath = '/districts';
  private programsPath = '/programs';
  private teacherCoachPath = '/teacher-coach-assignments';

  // ===== TENANT ID UTILITIES =====

  /**
   * Build tenant ID from components
   * Format: {state_id}:{district_id}:{school_id}
   */
  buildTenantId(stateId: string, districtId: string, schoolId: string): string {
    return `${stateId}:${districtId}:${schoolId}`;
  }

  /**
   * Parse tenant ID into components
   */
  parseTenantId(tenantId: string): { stateId: string; districtId: string; schoolId: string } | null {
    const parts = tenantId.split(':');
    if (parts.length !== 3) {
      console.warn(`Invalid tenant ID format: ${tenantId}. Expected format: state:district:school`);
      return null;
    }
    return {
      stateId: parts[0],
      districtId: parts[1],
      schoolId: parts[2]
    };
  }

  // ===== USER CRUD OPERATIONS =====

  /**
   * Get list of users with optional filtering
   */
  async getUsers(params?: UserListParams): Promise<UserResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<UserResponse>(`${this.basePath}${queryString}`);
  }

  /**
   * Get a specific user by ID
   */
  async getUser(userId: string): Promise<User> {
    return apiHelpers.get<User>(`${this.basePath}/${userId}`);
  }

  /**
   * Get user by Cognito username (for authentication)
   */
  async getUserByCognitoUsername(cognitoUsername: string): Promise<User> {
    return apiHelpers.get<User>(`${this.basePath}/cognito/${encodeURIComponent(cognitoUsername)}`);
  }

  /**
   * Get user by email address (for authentication fallback)
   */
  async getUserByEmail(email: string): Promise<User> {
    return apiHelpers.get<User>(`${this.basePath}/email/${encodeURIComponent(email)}`);
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserData): Promise<User & { temporaryPassword: string }> {
    return apiHelpers.post<User & { temporaryPassword: string }>(this.basePath, userData);
  }

  /**
   * Update an existing user (profile data only)
   */
  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    return apiHelpers.put<User>(`${this.basePath}/${userId}`, userData);
  }

  /**
   * Delete a user
   */
  async deleteUser(userId: string): Promise<void> {
    await apiHelpers.delete(`${this.basePath}/${userId}`);
  }

  // ===== ROLE AND ORGANIZATION MANAGEMENT =====

  /**
   * Assign a role to a user (separate from profile updates - replaces existing role)
   */
  async assignUserRole(userId: string, role: string): Promise<User> {
    return apiHelpers.put<User>(`${this.basePath}/${userId}/roles`, { role });
  }

  /**
   * Add a role to a user (admin-only operation - adds without replacing)
   */
  async addUserRole(userId: string, role: string): Promise<User> {
    try {
      return await apiHelpers.post<User>(`${this.basePath}/${userId}/roles`, { role });
    } catch (error: any) {
      console.error('Failed to add user role:', error);
      throw new Error(error.response?.data?.message || 'Failed to add role to user');
    }
  }

  /**
   * Assign organizations to a user (admin-only operation)
   */
  async assignUserOrganizations(userId: string, organizations: {
    districtId: string | undefined;
    schoolId: string | undefined;
    stateId: string;
    tenantId: string
  }): Promise<User> {
    return apiHelpers.put<User>(`${this.basePath}/${userId}/organizations`, organizations);
  }

  // ===== ORGANIZATION-SPECIFIC USER QUERIES =====

  /**
   * Get users by school
   */
  async getUsersBySchool(schoolId: string, params?: {
    role?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<UserResponse>(`${this.schoolsPath}/${schoolId}/users${queryString}`);
  }

  /**
   * Get users by district
   */
  async getUsersByDistrict(districtId: string, params?: {
    role?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<UserResponse>(`${this.districtsPath}/${districtId}/users${queryString}`);
  }

  /**
   * Get users by program
   */
  async getUsersByProgram(programId: string, params?: {
    role?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<UserResponse>(`${this.programsPath}/${programId}/users${queryString}`);
  }

  // ===== BACKWARDS COMPATIBILITY =====

  /**
   * Get users by organization (backwards compatibility)
   * @deprecated Use getUsersBySchool, getUsersByDistrict, or getUsersByProgram instead
   */
  async getUsersByOrganization(organizationId: string, params?: { limit?: number; offset?: number }): Promise<UserResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<UserResponse>(`/organizations/${organizationId}/users${queryString}`);
  }

  // ===== TEACHER-COACH ASSIGNMENTS =====

  /**
   * Get all teacher-coach assignments with filtering (NEW)
   */
  async getTeacherCoachAssignments(params?: {
    teacherId?: string;
    coachId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<TeacherCoachAssignmentResponse> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    return apiHelpers.get<TeacherCoachAssignmentResponse>(`${this.teacherCoachPath}${queryString}`);
  }

  /**
   * Assign teacher to coach (FIXED - using camelCase)
   */
  async assignTeacherToCoach(teacherId: string, coachId: string, organizationContext?: {
    schoolId?: string;
    districtId?: string;
    programId?: string;
  }): Promise<TeacherCoachAssignment> {
    return apiHelpers.post<TeacherCoachAssignment>(this.teacherCoachPath, {
      teacherId,  // FIXED: was teacher_id
      coachId,    // FIXED: was coach_id
      ...organizationContext,
    });
  }

  /**
   * Get coach assignments for a teacher (FIXED - now matches backend route)
   */
  async getTeacherCoaches(teacherId: string): Promise<{
    teacherId: string;
    teacherName: string;
    coaches: Array<{
      assignmentId: string;
      coachId: string;
      coachName: string;
      coachEmail: string;
      schoolId?: string;
      districtId?: string;
      assignedBy: string;
      assignedAt: string;
    }>;
  }> {
    return apiHelpers.get<{
      teacherId: string;
      teacherName: string;
      coaches: Array<{
        assignmentId: string;
        coachId: string;
        coachName: string;
        coachEmail: string;
        schoolId?: string;
        districtId?: string;
        assignedBy: string;
        assignedAt: string;
      }>;
    }>(`${this.basePath}/${teacherId}/coaches`);
  }

  /**
   * Get teacher assignments for a coach (FIXED - now matches backend route)
   */
  async getCoachTeachers(coachId: string): Promise<{
    coachId: string;
    coachName: string;
    teachers: Array<{
      assignmentId: string;
      teacherId: string;
      teacherName: string;
      teacherEmail: string;
      schoolId?: string;
      districtId?: string;
      subjects?: string[];
      grades?: string[];
      assignedBy: string;
      assignedAt: string;
    }>;
  }> {
    return apiHelpers.get<{
      coachId: string;
      coachName: string;
      teachers: Array<{
        assignmentId: string;
        teacherId: string;
        teacherName: string;
        teacherEmail: string;
        schoolId?: string;
        districtId?: string;
        subjects?: string[];
        grades?: string[];
        assignedBy: string;
        assignedAt: string;
      }>;
    }>(`${this.basePath}/${coachId}/teachers`);
  }

  /**
   * Remove teacher-coach assignment
   */
  async removeTeacherCoachAssignment(assignmentId: string): Promise<void> {
    await apiHelpers.delete(`${this.teacherCoachPath}/${assignmentId}`);
  }

  // ===== SEARCH AND PROFILE =====

  /**
   * Search users by name or email (within admin's scope)
   */
  async searchUsers(query: string, params?: {
    role?: string;
    subject?: string;
    grade?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    const searchParams = {
      search: query,
      ...params,
    };
    const queryString = apiHelpers.buildQueryString(searchParams);
    return apiHelpers.get<UserResponse>(`${this.basePath}/search${queryString}`);
  }

  /**
   * Get current user's profile
   */
  async getCurrentUserProfile(): Promise<User> {
    return apiHelpers.get<User>(`${this.basePath}/profile`);
  }

  /**
   * Update current user's profile (NEW - now matches backend route)
   */
  async updateCurrentUserProfile(userData: UpdateUserData): Promise<User> {
    return apiHelpers.put<User>(`${this.basePath}/profile`, userData);
  }

  // ===== BULK OPERATIONS =====

  /**
   * Preview bulk user creation from CSV
   */
  async previewBulkCreate(csvContent: string): Promise<BulkCreatePreview> {
    return apiHelpers.post<BulkCreatePreview>(`${this.basePath}/bulk/preview`, {
      csvContent
    });
  }

  /**
   * Approve and execute bulk user creation
   */
  async approveBulkCreate(bulkId: string, users: Array<{
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    subjects?: string[];
    grades?: string[];
    errors: string[];
    warnings: string[];
  }>): Promise<BulkCreateResult> {
    return apiHelpers.post<BulkCreateResult>(`${this.basePath}/bulk/approve`, {
      bulkId,
      users
    });
  }

  /**
   * Bulk create users (legacy method)
   * @deprecated Use previewBulkCreate and approveBulkCreate instead
   */
  async bulkCreateUsers(users: CreateUserData[]): Promise<{ success: number; failed: number; errors: any[] }> {
    return apiHelpers.post<{ success: number; failed: number; errors: any[] }>(`${this.basePath}/bulk`, { users });
  }

  // ===== EXPORT =====

  /**
   * Export users to CSV (NEW - now matches backend route)
   */
  async exportUsers(params?: UserListParams): Promise<Blob> {
    const queryString = params ? apiHelpers.buildQueryString(params) : '';
    const response = await apiHelpers.get<Blob>(`${this.basePath}/export${queryString}`, {
      responseType: 'blob',
    });
    return response;
  }

  // ===== SPECIALIZED QUERIES =====

  /**
   * Get teachers in a school
   */
  async getSchoolTeachers(schoolId: string, params?: {
    subject?: string;
    grade?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    return this.getUsersBySchool(schoolId, {
      role: 'Teacher',
      ...params
    });
  }

  /**
   * Get coaches in a school
   */
  async getSchoolCoaches(schoolId: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    return this.getUsersBySchool(schoolId, {
      role: 'Coach',
      ...params
    });
  }

  /**
   * Get district admins
   */
  async getDistrictAdmins(districtId: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    return this.getUsersByDistrict(districtId, {
      role: 'DistrictAdmin',
      ...params
    });
  }

  /**
   * Get all teachers in a district (across schools)
   */
  async getDistrictTeachers(districtId: string, params?: {
    subject?: string;
    grade?: string;
    page?: number;
    pageSize?: number;
  }): Promise<UserResponse> {
    return this.getUsersByDistrict(districtId, {
      role: 'Teacher',
      ...params
    });
  }
}

// Export singleton instance
export default new UserService();